import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, BoxProps, Button, ButtonGroup, Center, Heading, HStack, Spinner, Stack } from '@chakra-ui/react';
import { useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { ErrorState } from '@src/components/sco/ErrorState';
import { InvestorSearchPagination } from '@src/components/sco/InvestorSearch/Layout/InvestorSearchContext';
import { MembersCardGrid } from '@src/components/sco/Members/MembersCardGrid';
import { MemberCard } from '@src/components/sco/Members/MembersCardGrid/MemberCard';
import { MembersTable } from '@src/components/sco/Members/MembersTable';
import { MembersTableRow } from '@src/components/sco/Members/MembersTable/MembersTableRow';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { ScoUserCollection } from '@src/graphql-types';
import { useQueryStateSet } from '@src/hooks/useQueryStateSet';
import { gql } from '@src/lib/gql';
import { parseAsArrayOf, parseAsBoolean, parseAsFloat, parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import React, { useEffect, useState } from 'react';
import { LuGrid3X3, LuList } from 'react-icons/lu';
export type MemberSearchFilters = {
  companyIndustryKeys?: string[];
  companyStageKeys?: string[];
  distanceInMiles?: number;
  isVerified?: boolean;
  keywords?: string;
  latitude?: number;
  location?: string;
  locationCountry?: string;
  locationRegion?: string;
  longitude?: number;
  matchAll?: boolean;
};
export type MemberSearchPagination = {
  page?: number;
  pageSize?: number;
};
export type MemberSearchResultProps = {
  containerProps?: BoxProps;
  title?: string;
};
export const useMemberSearchPagination = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      page: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(24)
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQueryStateSet(t0);
};
export const useMemberSearchFilters = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      companyIndustryKeys: parseAsArrayOf(parseAsString),
      companyStageKeys: parseAsArrayOf(parseAsString),
      distanceInMiles: parseAsInteger,
      isVerified: parseAsBoolean,
      keywords: parseAsString,
      latitude: parseAsFloat,
      location: parseAsString,
      locationCountry: parseAsString,
      locationRegion: parseAsString,
      longitude: parseAsFloat,
      matchAll: parseAsBoolean
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQueryStateSet(t0);
};
export const MemberSearchResults = (props: MemberSearchResultProps) => {
  const layout = useParentAppLayout();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const [view, setView] = useQueryState<'grid' | 'table' | string>('view', parseAsString.withDefault('grid'));
  const [filters, filterValues] = useMemberSearchFilters();
  const [pagination, paginationValues] = useMemberSearchPagination();
  const [results, setResults] = useState<ScoUserCollection>();
  const query = useQuery<{
    scoMembers: ScoUserCollection;
  }>(gql`
    query (
      $keywords: String
      $location: String
      $companyIndustryKeys: [String]
      $companyStageKeys: [String]
      $page: Int
      $pageSize: Int
    ) {
      scoMembers(
        keywords: $keywords
        location: $location
        companyIndustryKeys: $companyIndustryKeys
        companyStageKeys: $companyStageKeys
        page: $page
        pageSize: $pageSize
      ) {
        id
        items {
          id
          ${MemberCard.fragments.ScoUser()}
          ${MembersTableRow.fragments.ScoUser()}
        }
        pagination {
          hasNextPage
          hasPreviousPage
          itemCount
          pageSize
          page
          pageCount
        }
      }
    }
  `, {
    returnPartialData: true,
    variables: {
      ...filterValues,
      ...paginationValues
    }
  });
  useEffect(() => {
    setResults(query.data?.scoMembers);
  }, Object.values(filterValues));
  useEffect(() => {
    if (query.data?.scoMembers) {
      setResults(query.data?.scoMembers);
    }
  }, [query.data?.scoMembers]);
  return <Box {...props.containerProps}>
      <Stack align={'center'} direction={getBreakpointValue(['column', 'column', 'row'])} justify={getBreakpointValue(['stretch', 'stretch', 'space-between'])}>
        <Heading>{props.title || 'Members'}</Heading>
        <Box>
          <HStack align={'center'} justify={'center'}>
            <ButtonGroup attached={true} size={'sm'}>
              <Button aria-label={'View as Grid'} variant={view == 'grid' ? 'solid' : 'outline'} onClick={() => setView('grid')}>
                <LuGrid3X3 />
                Grid
              </Button>
              <Button aria-label={'View as Table'} variant={view == 'table' ? 'solid' : 'outline'} onClick={() => setView('table')}>
                <LuList />
                List
              </Button>
            </ButtonGroup>
          </HStack>
        </Box>
      </Stack>
      {results && <Box paddingY={'1em'}>
          {view === 'table' ? <MembersTable filters={filters} loading={query.loading} membersCollection={results} onLoadMore={() => {
        pagination.page.setValue(paginationValues.page + 1);
      }} /> : <MembersCardGrid filters={filters} loading={query.loading} membersCollection={results} onLoadMore={() => {
        pagination.page.setValue(paginationValues.page + 1);
      }} />}
        </Box>}
      {query.error && <ErrorState error={query.error} />}
      {query.loading && <Center padding={'2em'}>
          <Spinner />
        </Center>}
    </Box>;
};